import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_PATHS, API_QUERY_PARAMS } from '../constants/api-paths';
import { jsonHttpHeaders } from '../constants/http-headers';
import {
  DocumentTechniqueExpiration,
  DocumentTechniqueType,
  DocumentTechniqueUploadParams,
  LogementDocumentTechnique,
} from '../models/document-technique.model';

/**
 * Client HTTP du dossier technique d'un logement
 * ({@code /logements/{id}/documents-techniques}).
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentTechniqueService {
  private readonly headers = jsonHttpHeaders();

  constructor(private readonly http: HttpClient) {}

  /** Liste les documents actifs ; filtre optionnel par type. */
  list(
    logementId: string,
    type?: DocumentTechniqueType
  ): Observable<LogementDocumentTechnique[]> {
    let params = new HttpParams();
    if (type) {
      params = params.set(API_QUERY_PARAMS.type, type);
    }
    return this.http.get<LogementDocumentTechnique[]>(
      `${environment.apiUrl}${API_PATHS.logementDocumentsTechniques(logementId)}`,
      { params }
    );
  }

  /** Métadonnées d'un document actif. */
  findById(
    logementId: string,
    documentId: string
  ): Observable<LogementDocumentTechnique> {
    return this.http.get<LogementDocumentTechnique>(
      `${environment.apiUrl}${API_PATHS.logementDocumentTechnique(
        logementId,
        documentId
      )}`
    );
  }

  /** Upload multipart (fichier + type / date / résultat). */
  upload(
    logementId: string,
    params: DocumentTechniqueUploadParams
  ): Observable<LogementDocumentTechnique> {
    const formData = new FormData();
    formData.append('file', params.file, params.file.name);

    let httpParams = new HttpParams()
      .set('type', params.type)
      .set('dateDiagnostic', params.dateDiagnostic);
    if (params.resultat) {
      httpParams = httpParams.set('resultat', params.resultat);
    }

    return this.http.post<LogementDocumentTechnique>(
      `${environment.apiUrl}${API_PATHS.logementDocumentsTechniques(logementId)}`,
      formData,
      { params: httpParams }
    );
  }

  /** Contenu binaire du fichier stocké. */
  download(logementId: string, documentId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${API_PATHS.logementDocumentTechniqueFichier(
        logementId,
        documentId
      )}`,
      { responseType: 'blob' }
    );
  }

  /** Met à jour la date d'expiration ({@code null} = illimité). */
  updateExpiration(
    logementId: string,
    documentId: string,
    dateExpiration: string | null
  ): Observable<LogementDocumentTechnique> {
    const body: DocumentTechniqueExpiration = {
      dateExpirationDto: dateExpiration,
    };
    return this.http.put<LogementDocumentTechnique>(
      `${environment.apiUrl}${API_PATHS.logementDocumentTechniqueExpiration(
        logementId,
        documentId
      )}`,
      body,
      { headers: this.headers }
    );
  }

  /** Suppression côté API (métadonnées + stockage). */
  delete(logementId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${API_PATHS.logementDocumentTechnique(
        logementId,
        documentId
      )}`
    );
  }
}
